import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Tag,
  Briefcase,
  Server,
  FolderArchive,
  Clock,
  Zap,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison directement chez vous ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos fournitures de déménagement directement chez
            vous {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que vos préparatifs demandent toute votre attention,
            notre service de proximité prend en charge l&apos;intégralité
            du transport de vos équipements avec trois engagements
            fermes.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Livraison à plat et à domicile",
              body: "Nous déposons vos commandes directement sur le pas de votre porte à Jouy-en-Josas ainsi que dans les communes voisines (Buc, Les Loges-en-Josas, Vélizy-Villacoublay, Versailles).",
              Icon: Truck,
            },
            {
              title: "Respect strict du planning",
              body: "Nous convenons d'un créneau horaire précis pour vous éviter toute attente inutile et vous permettre de commencer vos colis sans perdre une minute.",
              Icon: Calendar,
            },
            {
              title: "Protection totale du matériel",
              body: "Vos fournitures vous sont remises dans un état de propreté impeccable, 100% sèches et prêtes à être pliées.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Déménagement d'entreprise ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Déménagement professionnel
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Cartons pour déménagement d&apos;entreprise : la haute
              résistance pour les professionnels
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Le transfert de bureaux, de commerces ou d&apos;administrations
              {" "}{city.aCity} ne s&apos;improvise pas et répond à des
              impératifs d&apos;efficacité stricts.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Des formats techniques adaptés",
                  body: "Nous mettons à disposition des entreprises des cartons d'archives à poignées renforcées, des caisses de transport pour matériel informatique et des bacs de manutention ultra-rigides. Ces équipements pros protègent vos serveurs, vos dossiers confidentiels et vos outils de travail contre les chocs.",
                  Icon: Server,
                },
                {
                  title: "Optimisation de vos transferts",
                  body: "Nos fournitures professionnelles sont calibrées pour s'empiler parfaitement sur des chariots de manutention. Cela permet à vos équipes ou à vos transporteurs de vider les bureaux à toute vitesse, réduisant au maximum le temps d'arrêt de votre activité économique.",
                  Icon: Layers,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
                >
                  <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/cartons-demenagement"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-jouy-en-josas-cta-pro"
            >
              <Briefcase className="h-4 w-4" strokeWidth={2.6} />
              Voir nos solutions pro
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Briefcase className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Transfert d&apos;activité maîtrisé
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Bureaux, commerces, administrations : un matériel calibré
              pour minimiser le temps d&apos;arrêt de votre activité.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <FolderArchive className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Cartons archives à poignées
              </div>
              <div className="flex items-center gap-2">
                <Server className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Caisses serveurs &amp; informatique
              </div>
              <div className="flex items-center gap-2">
                <Boxes className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Bacs manutention ultra-rigides
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Compatible chariots roulants
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Gain de temps avant le jour J ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Gain de temps
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Gain de temps avant le jour J : optimisez vos préparatifs
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Une bonne méthode associée à un matériel standardisé vous
            fait gagner de précieuses heures durant la phase de
            cartonnerie.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              n: "01",
              title: "Suppression des trajets inutiles",
              body: "En commandant votre kit complet en ligne avec livraison à domicile, vous évitez les allers-retours épuisants dans les zones commerciales des Yvelines.",
              Icon: Truck,
            },
            {
              n: "02",
              title: "Montage ultra-rapide des boîtes",
              body: "Nos cartons pros se déploient et se plient au millimètre près en quelques secondes, sans forcer sur les rabats.",
              Icon: Zap,
            },
            {
              n: "03",
              title: "Marquage direct efficace",
              body: "Les zones pré-imprimées sur nos cartons vous permettent d'indiquer instantanément le contenu et le bureau ou la pièce cible.",
              Icon: Tag,
            },
            {
              n: "04",
              title: "Empilage stable immédiat",
              body: "Grâce à des dimensions standardisées, vous pouvez empiler vos cartons proprement dans un coin sans encombrer vos pièces avant l'arrivée du camion.",
              Icon: Layers,
            },
            {
              n: "05",
              title: "Fermeture en un geste",
              body: "L'utilisation de notre dévidoir pistolet pro vous permet de sceller une boîte en un seul mouvement fluide, sans chercher le bout de l'adhésif.",
              Icon: Clock,
            },
          ].map(({ n, title, body, Icon }) => (
            <div
              key={n}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5]">
                  Étape&nbsp;{n}
                </div>
                <h3 className="mt-1 font-display font-extrabold text-[16px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-jouy-en-josas-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits complets clés en main
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-jouy-en-josas-cta-accessoires"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Dévidoir &amp; adhésifs pro
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-jouy-en-josas-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
